import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["isGlobalStoreEnabled"];
import React, { useMemo } from 'react';
import { ErrorBoundary, GroupingMessages, NotificationsCommonProviders, NotificationsExperienceTrackerProvider } from '@atlassian/notification-common';
import { ExperienceMark, ExperienceStart, getNotificationMountStartMark, NotificationExperienceMarkKeys, useExperienceTracker } from '@atlassian/notification-common/experience-tracking';
import { ExperienceIds, Experiences, SloFailureReason } from '@atlassian/notification-common/types';
import { FilterStoreContainer, StoreContainer, useNotificationStateFilters } from '@atlassian/notification-store';
import { packageName, packageVersion } from '../../common/constants';
import { defaultAnalyticsContextData, NOTIFICATION_COMPONENT } from '../../common/utils/analytics';
import { commonExperienceConfig } from '../../common/utils/ufo-helpers';
import ConnectedNotificationGroupList from './connected-notification-groups-list';
import { notificationListCategoryFilters, notificationListProductFilters } from './constants';
import { NotificationListLayoutWrapper } from './styled';
var Notifications = function Notifications(_ref) {
  var testId = _ref.testId,
    userId = _ref.userId,
    cloudId = _ref.cloudId,
    product = _ref.product,
    ThirdPartyNotificationNudge = _ref.ThirdPartyNotificationNudge,
    emojiProvider = _ref.emojiProvider,
    mentionProvider = _ref.mentionProvider,
    onCommentCreatedFn = _ref.onCommentCreatedFn,
    isEligibleForBellNotifRepliesCtxExp = _ref.isEligibleForBellNotifRepliesCtxExp;
  var experienceTracker = useExperienceTracker();
  var _useNotificationState = useNotificationStateFilters(),
    _useNotificationState2 = _slicedToArray(_useNotificationState, 1),
    filters = _useNotificationState2[0];
  var mountStartTime = useMemo(function () {
    return getNotificationMountStartMark()[1];
  }, []);
  var errorBoundaryExperienceCallback = function errorBoundaryExperienceCallback() {
    experienceTracker.fail({
      name: Experiences.RENDER_NOTIFICATIONS_WITHOUT_CONTENT,
      error: new Error(SloFailureReason.ROOT_ERROR_BOUNDARY),
      attributes: {
        failureReason: SloFailureReason.ROOT_ERROR_BOUNDARY
      }
    });
    experienceTracker.fail({
      name: Experiences.RENDER_NOTIFICATIONS_WITH_CONTENT,
      error: new Error(SloFailureReason.ROOT_ERROR_BOUNDARY),
      attributes: {
        failureReason: SloFailureReason.ROOT_ERROR_BOUNDARY
      }
    });
  };
  var analyticsContext = useMemo(function () {
    return {
      cloudId: cloudId,
      product: product,
      readStateFilter: filters.readStateFilter,
      requestCategory: filters.categoryFilter,
      productFilter: filters.productFilter,
      screenName: 'notificationList',
      view: 'grouped'
    };
  }, [cloudId, product, filters.readStateFilter, filters.categoryFilter, filters.productFilter]);
  return /*#__PURE__*/React.createElement(NotificationsCommonProviders, {
    packageName: packageName,
    packageVersion: packageVersion,
    defaultAnalyticsContextData: defaultAnalyticsContextData,
    analyticsContext: analyticsContext,
    userId: userId
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    subjectId: "root",
    onErrorCallback: errorBoundaryExperienceCallback,
    isCritical: true
  }, /*#__PURE__*/React.createElement(ExperienceStart, {
    name: Experiences.RENDER_NOTIFICATIONS_WITHOUT_CONTENT,
    id: ExperienceIds.FIRST_LOAD
  }), /*#__PURE__*/React.createElement(ExperienceStart, {
    name: Experiences.RENDER_NOTIFICATIONS_WITH_CONTENT,
    id: ExperienceIds.FIRST_LOAD,
    collectAll: [{
      name: Experiences.RENDER_NOTIFICATION_PAGE_WITH_CONTENT
    }]
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATIONS_INITIAL_MOUNT,
    phase: "start",
    timestamp: mountStartTime
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATIONS_INITIAL_MOUNT,
    phase: "end"
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATIONS_FEED_SKELETON_MOUNT,
    phase: "start"
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATION_LIGHTWEIGHT_RENDER,
    phase: "start"
  }), /*#__PURE__*/React.createElement(NotificationListLayoutWrapper, {
    "data-testid": testId,
    role: "dialog",
    "aria-labelledby": "notification-list-header-label"
  }, /*#__PURE__*/React.createElement(ConnectedNotificationGroupList, {
    product: product,
    ThirdPartyNotificationNudge: ThirdPartyNotificationNudge,
    emojiProvider: emojiProvider,
    mentionProvider: mentionProvider,
    onCommentCreatedFn: onCommentCreatedFn,
    isEligibleForBellNotifRepliesCtxExp: isEligibleForBellNotifRepliesCtxExp
  })), /*#__PURE__*/React.createElement(GroupingMessages, null)));
};
var ContainedNotification = function ContainedNotification(props) {
  var isGlobalStoreEnabled = props.isGlobalStoreEnabled,
    restProps = _objectWithoutProperties(props, _excluded);
  var ufoAttributes = useMemo(function () {
    var _getNotificationMount = getNotificationMountStartMark(),
      _getNotificationMount2 = _slicedToArray(_getNotificationMount, 2),
      mountStartTimestamp = _getNotificationMount2[1];
    return {
      component: NOTIFICATION_COMPONENT,
      experienceConfig: commonExperienceConfig,
      customStartTime: _defineProperty(_defineProperty({}, Experiences.RENDER_NOTIFICATIONS_WITHOUT_CONTENT, mountStartTimestamp), Experiences.RENDER_NOTIFICATIONS_WITH_CONTENT, mountStartTimestamp)
    };
  }, []);
  return /*#__PURE__*/React.createElement(NotificationsExperienceTrackerProvider, {
    ufoAttributes: ufoAttributes
  }, /*#__PURE__*/React.createElement(FilterStoreContainer, {
    categoryFilters: notificationListCategoryFilters,
    productFilters: notificationListProductFilters
  }, /*#__PURE__*/React.createElement(StoreContainer, {
    isGlobal: props.isGlobalStoreEnabled
  }, /*#__PURE__*/React.createElement(Notifications, restProps))));
};
export default ContainedNotification;